'use client';

import { useCallback } from 'react';

import { TriangleAlert } from 'lucide-react';

import { ErrorShell, type ErrorShellAction } from './error-shell';

interface ErrorScreenProps {
	title?: string;
	description?: string;
	digest?: string;
	/** Label on the primary action. `Try again` for reset-capable route boundaries. */
	reloadLabel?: string;
	onReload: () => void;
	onBack?: () => void;
	onYellAtUsClick?: () => void;
	onView?: () => void;
}

const YELL_OPENERS = [
	'Hey @naumu_ai, ',
	'@naumu_ai pls fix, ',
	'@naumu_ai your app just yelled at me first, ',
	'@naumu_ai found a bug: ',
	'Hey @naumu_ai, small problem: ',
	'@naumu_ai you need to know about this: ',
	'@naumu_ai why does this happen: ',
	'Hey @naumu_ai, the page broke: ',
	'@naumu_ai here is what happened: ',
	"Hey @naumu_ai, I'm yelling: ",
];

const getYellTweetUrl = () => {
	const opener = YELL_OPENERS[Math.floor(Math.random() * YELL_OPENERS.length)];
	return `https://x.com/intent/tweet?text=${encodeURIComponent(opener)}`;
};

const XLogo = ({ className }: { className?: string }) => (
	<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
		<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
	</svg>
);

export const ErrorScreen = ({
	title = "This page couldn't load",
	description = 'Reload to try again, or go back.',
	digest,
	reloadLabel = 'Reload',
	onReload,
	onBack,
	onYellAtUsClick,
	onView,
}: ErrorScreenProps) => {
	const handleYellAtUs = useCallback(() => {
		onYellAtUsClick?.();
		if (typeof window !== 'undefined') {
			window.open(getYellTweetUrl(), '_blank', 'noopener,noreferrer');
		}
	}, [onYellAtUsClick]);

	const actions: ErrorShellAction[] = [
		{ label: reloadLabel, onClick: onReload, variant: 'primary' },
	];
	if (onBack) {
		actions.push({ label: 'Back', onClick: onBack, variant: 'secondary' });
	}

	return (
		<ErrorShell
			icon={<TriangleAlert className="size-12" strokeWidth={1.5} />}
			title={title}
			description={description}
			extra={
				<button
					type="button"
					onClick={handleYellAtUs}
					className="flex items-center gap-2 rounded-[18px] border border-border-muted bg-neutral/5 px-9 py-6 text-[13px] font-medium text-neutral-muted transition-colors hover:bg-neutral/10 hover:text-neutral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer"
				>
					<span>Yell at us on</span>
					<XLogo className="size-3.5 shrink-0" />
				</button>
			}
			actions={actions}
			digest={digest}
			onView={onView}
		/>
	);
};
